<template>
  <showcase-layout class="main-page">
    <template #top>
      <content-container class="content-padding">
        <a-row class="top">
          <a-col :span="12">
            <main-title class="main-title">
              <template v-slot:default>ПРОЕКТЫ<br />НГУ</template>
              <template v-slot:description>
                {{ `${projectsCount} активных проектов в работе` }}
              </template>
            </main-title>
            <div class="top-search__wrapper">
              <input-search
                class="mb-40"
                page="search"
                placeholder="Ищите интересные Вам проекты"
                page-name="showcase-main-page"
                :searchString="searchString"
                @update="handleUpdateSearchString"
              />
            </div>
          </a-col>
          <a-col :span="12">
            <a-row class="main-gallery">
              <a-col class="main-gallery-col">
                <div>
                  <a-col class="container">
                    <img src="./assets/images/innovations.png" alt="" />
                    <tag-item class="tag">#innovations</tag-item>
                  </a-col>

                  <a-col class="container">
                    <img src="./assets/images/space_exploration.png" alt="" />
                    <tag-item class="tag">#space exploration</tag-item>
                  </a-col>
                </div>
              </a-col>
              <a-col class="main-gallery-col">
                <div>
                  <a-col class="container">
                    <img src="./assets/images/biomedicine.png" alt="" />
                    <tag-item class="tag">#biomedicine</tag-item>
                  </a-col>

                  <a-col class="container">
                    <img src="./assets/images/nanotechnology.png" alt="" />
                    <tag-item class="tag">#nanotechnology</tag-item>
                  </a-col>
                </div>
              </a-col>
            </a-row>
          </a-col>
        </a-row>
      </content-container>
    </template>
    <template v-slot:default>
      <div class="showcase-header__wrapper">
        <content-container>
          <a-row class="showcase-header" :wrap="false" :gutter="20">
            <a-col class="sorting">
              <a-row align="middle" :wrap="false">
                <span class="sorting__label">Сортировать</span>
                <a-select
                  class="sorting__select"
                  :options="sortingList"
                  v-model:value="sorting"
                />
              </a-row>
            </a-col>
            <a-col
              :class="[
                isVisibleShowcaseHeaderSearch ? 'visible' : '',
                'showcase-search__wrapper',
              ]"
            >
              <input-search
                placeholder="Ищите интересные Вам проекты"
                page-name="showcase-main-page"
                :searchString="searchString"
                @update="handleUpdateSearchString"
              />
            </a-col>
            <a-col>
              <a-row :gutter="10">
                <a-col>
                  <a-badge
                    :count="countFilter"
                    :number-style="{
                      backgroundColor: '#72c81e',
                      color: '#fff',
                    }"
                  >
                    <button-primary @click="openFilter = true">
                      Фильтр
                    </button-primary>
                  </a-badge>
                  <ProjectFilterPanel
                    :is-open="openFilter"
                    :filter-types="filterTypes"
                    :filter-statuses="filterStatuses"
                    :filter-tags="filterTags"
                    @update:filter="applyFilter"
                    @reset-filter="resetFilter"
                    @close="openFilter = false"
                  />
                </a-col>
              </a-row>
            </a-col>
          </a-row>
        </content-container>
      </div>
      <div ref="scrollElement" class="scroll-area">
        <content-container>
          <a-spin
            :spinning="isLoading"
            class="custom-spinner"
            style="height: 100vh; top: 50%"
          >
            <a-row :gutter="[20, 20]" class="projects">
              <a-col
                :lg="12"
                :xl="8"
                v-for="(project, index) in projects"
                :key="++index"
                @click="router.push(`/showcase/project/${project.id}`)"
              >
                <project-card :project="project" />
              </a-col>
            </a-row>
            <a-back-top :visibilityHeight="1000" />
          </a-spin>
        </content-container>
      </div>
    </template>
  </showcase-layout>
</template>

<script setup>
  import ShowcaseLayout from '@/layouts/ShowcaseLayout.vue';
  import InputSearch from '@/views/projects-showcase/components/InputSearch.vue';
  import MainTitle from '@/views/projects-showcase/components/MainTitle.vue';
  import TagItem from '@/components/tags/TagItem.vue';
  import ContentContainer from '@/views/portal/components/ContentContainer.vue';
  import ButtonPrimary from '@/views/portal/components/UI/ButtonPrimary.vue';
  import ProjectCard from '@/views/projects-showcase/components/ProjectCard.vue';
  import { useApi } from '@/api/use-api';
  import { useRoute, useRouter } from 'vue-router';
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import {
    PROJECT_TYPE_ID_EDUCATION,
    PROJECT_TYPE_ID_EVENT,
    PROJECT_TYPE_ID_EXPERTISE,
    PROJECT_TYPE_ID_PARTNERSHIP,
    PROJECT_TYPE_ID_RESEARCH,
    PROJECT_TYPE_ID_RESOURCES,
    PROJECT_TYPE_ID_PRODUCT,
    PROJECT_TYPE_ID_STARTUP,
  } from '@/api/models/ProjectType';
  import { debounce } from 'lodash-es';
  import ProjectFilterPanel from '@/views/projects-showcase/components/ProjectFilterPanel.vue';

  const api = useApi();
  const route = useRoute();
  const router = useRouter();
  const sorting = ref('indexes');
  const sortingList = [
    { value: 'name', label: 'По алфавиту' },
    { value: 'indexes', label: 'По количеству показателей (убывание)' },
  ];
  const projects = ref([]);
  const projectsCount = ref('-');
  const isLoading = ref(false);
  const isVisibleShowcaseHeaderSearch = ref(false);
  const searchString = ref('');

  const scrollElement = ref(null);
  const PAGE_SIZE = 20;
  const totalPages = ref(0);
  const lastPage = ref(0);
  const currentPage = ref(1);

  /* Filter */
  const openFilter = ref(false);
  const filterTags = ref([]);
  const filterTypes = ref([
    {
      value: PROJECT_TYPE_ID_RESEARCH,
      label: 'Проведение исследовательских работ',
      checked: false,
    },
    { value: PROJECT_TYPE_ID_EDUCATION, label: 'Образование', checked: false },
    {
      value: PROJECT_TYPE_ID_EXPERTISE,
      label: 'Проведение экспертизы',
      checked: false,
    },
    {
      value: PROJECT_TYPE_ID_PARTNERSHIP,
      label: 'Новое сотрудничество',
      checked: false,
    },
    {
      value: PROJECT_TYPE_ID_RESOURCES,
      label: 'Использование ресурсов',
      checked: false,
    },
    { value: PROJECT_TYPE_ID_EVENT, label: 'Мероприятия', checked: false },
    {
      value: PROJECT_TYPE_ID_PRODUCT,
      label: 'Создание продукта',
      checked: false,
    },
    {
      value: PROJECT_TYPE_ID_STARTUP,
      label: 'Стартап',
      checked: false,
    },
  ]);
  const filterStatuses = ref([
    {
      value: 'isLookingForPartner',
      label: 'Приглашаем партнеров',
      checked: false,
    },
    {
      value: 'isLookingForStudent',
      label: 'Приглашаем студентов',
      checked: false,
    },
    {
      value: 'isLookingForPerformer',
      label: 'Ищем исполнителей',
      checked: false,
    },
    { value: 'isWaiting', label: 'Выполним похожий проект', checked: false },
  ]);
  /*---*/

  onMounted(async () => {
    await getProjectsCount();
    await getProjects();
    window.addEventListener('scroll', handleScroll);
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
  });

  watch(
    () => route.query.textSearch,
    async () => {
      await applyFilter();
    }
  );

  watch(
    () => route.query.status,
    async () => {
      await applyFilter();
    }
  );

  watch(sorting, (filter) => {
    sortingProjects(filter);
  });

  const handleScroll = debounce(() => {
    const scrollY = window.scrollY;
    isVisibleShowcaseHeaderSearch.value = scrollY >= 650;

    const elementBottom = scrollElement.value?.getBoundingClientRect().bottom;
    if (elementBottom && elementBottom < window.innerHeight) {
      if (
        lastPage.value === currentPage.value &&
        currentPage.value < totalPages.value
      ) {
        currentPage.value++;
        getProjects();
      }
    }
  }, 70);

  const sortingProjects = (filter) => {
    switch (filter) {
      case 'name':
        return projects.value.sort((a, b) =>
          a.name.trim().localeCompare(b.name.trim())
        );
      case 'indexes':
        return projects.value.sort((a, b) => b.indexesCount - a.indexesCount);
    }
  };

  const getProjects = async () => {
    if (currentPage.value === 1) isLoading.value = true;

    try {
      const searchParams = new URLSearchParams({
        'per-page': PAGE_SIZE,
        page: currentPage.value,
        sort: '-indexes',
      });

      if (filterTypes.value.some((el) => el.checked)) {
        searchParams.append('types', flattenFilterParams(filterTypes.value));
      }

      filterStatuses.value.forEach((el) => {
        if (el.checked) {
          searchParams.append(el.value, '1');
        }
      });

      if (filterTags.value.length > 0) {
        searchParams.append('tags', flattenFilterParams(filterTags.value));
      }

      if (route.query.textSearch && route.query.textSearch.length > 0) {
        searchParams.append('textSearch', route.query.textSearch);
      }

      if (route.query.status) {
        switch (route.query.status) {
          case 'active':
            searchParams.append('status', 'active');
            break;
          case 'completed':
            searchParams.append('status', 'completed');
            break;
        }
      }

      const response = await api.publicProject.projects(searchParams);
      if (lastPage.value !== currentPage.value) {
        projects.value = projects.value.concat(response.projects.items);
        lastPage.value = currentPage.value;
      }
      totalPages.value = response.projects.pages.total;
    } catch (error) {
      console.error('error', error);
    } finally {
      sortingProjects(sorting.value);
      isLoading.value = false;
    }
  };

  const getProjectsCount = async () => {
    try {
      const response = await api.publicProject.projectsCount();
      projectsCount.value = response.count.toString();
    } catch (error) {
      console.error('error', error);
    }
  };

  const countFilter = computed(() => {
    const typesCount = filterTypes.value.filter((el) => el.checked).length;
    const statusesCount = filterStatuses.value.filter(
      (el) => el.checked
    ).length;
    const tagsCount = filterTags.value.length;

    return typesCount + statusesCount + tagsCount;
  });

  const resetFilter = async () => {
    filterTypes.value.forEach((el) => (el.checked = false));
    filterStatuses.value.forEach((el) => (el.checked = false));
    filterTags.value = [];
    await applyFilter();
  };

  const applyFilter = async (filters) => {
    projects.value = [];
    lastPage.value = 0;
    totalPages.value = 0;
    currentPage.value = 1;

    if (filters) {
      filters.tags && (filterTags.value = filters.tags);
      filters.types && (filterTypes.value = filters.types);
      filters.statuses && (filterStatuses.value = filters.statuses);
    }

    await getProjects();
  };

  const flattenFilterParams = (filterParams) => {
    return filterParams
      .filter((el) => typeof el === 'string' || el.checked)
      .reduce(
        (acc, el, currentIndex) =>
          acc +
          (currentIndex === 0 ? '' : ',') +
          (typeof el === 'string' ? el : el.value),
        ''
      );
  };

  const handleUpdateSearchString = (value) => {
    searchString.value = value;
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/styles/_breakpoints.scss' as *;

  * {
    font-family: Nunito Sans, sans-serif;
  }

  .top {
    .top-search__wrapper {
      width: 440px;
    }

    .main-title {
      margin: 60px 0 100px;
    }

    .main-gallery {
      flex-wrap: nowrap;
      justify-content: end;
      gap: 20px;
      margin: 28px 0 0;

      .main-gallery-col {
        div {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        &:last-child {
          margin-top: 20px;
        }
      }

      .tag {
        bottom: 14px;
        left: 14px;
        position: absolute;
      }
    }

    @media (min-width: 768px) and (max-width: 1280px) {
      flex-direction: column;
      align-items: center;

      .main-gallery {
        justify-content: center;
      }
    }

    @media (max-width: 767px) {
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .main-title {
        flex-direction: row;
        gap: 10px;
        margin: 0;

        .main_title h1 {
          margin: 0;
        }
      }

      .main-gallery {
        justify-content: center;
      }

      .top-search__wrapper {
        width: 100%;
      }
    }
  }

  .sorting {
    .sorting__label {
      color: #718096;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      margin-right: 20px;
    }

    &__select {
      width: 208px;

      :deep(.ant-select-selection-item) {
        color: #aeb4dd;
      }
    }
  }

  .showcase-header__wrapper {
    background: #fff;
    position: sticky;
    top: 86px;
    z-index: 1;
  }

  .showcase-header {
    align-items: center;
    border-bottom: 1px solid #babbc2;
    justify-content: space-between;
    padding: 20px 0;

    @include breakpoint(max, md) {
      flex-wrap: wrap;
      gap: 10px;
      padding-inline: 20px;
    }
  }

  .showcase-search__wrapper {
    visibility: hidden;
    opacity: 0;
    max-width: 859px;
    width: 100%;
    transition: all 300ms ease-in-out;

    &.visible {
      visibility: visible;
      opacity: 1;
    }
  }

  .showcase__content {
    display: flex;

    &.cards {
      flex-direction: row;
      flex-wrap: wrap;

      .department-item {
        flex: 0 0 33.333%;
      }
    }

    &.table {
      flex-direction: column;
      flex-wrap: nowrap;
    }
  }

  .projects {
    margin-top: 40px;

    @include breakpoint(max, md) {
      padding-inline: 20px;
    }
  }

  .checkbox-item {
    margin-bottom: 0;
  }

  .carousel-wrapper {
    margin-top: 110px;
    margin-bottom: 90px;
  }

  blockquote {
    padding: 100px 0;
    margin: 0 auto;

    @include breakpoint(max, lg) {
      padding: 70px 0;
    }
  }

  .pict-text-block-wrapper {
    margin-bottom: 135px;
    @include breakpoint(max, lg) {
      margin-bottom: 80px;
    }
  }

  :deep(.content-container).content-padding {
    // padding-left: 40px;
    // padding-right: 40px;
    padding: 20px 0;
  }
</style>
